import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ProductSearchInput } from "@/components/products/ProductSearchInput";
import { ProductGrid, type SelectedProduct } from "./ProductGrid";
import { Product } from "@/types/product";
import { cn } from "@/lib/utils";
import { useRecentProducts } from "@/hooks/use-recent-products";
import { useProductSearch } from "@/hooks/use-product-search";
import { useProductEvents } from "@/hooks/use-product-events";
import { useFeatureFlagEnabled } from "posthog-js/react";

interface ProductCatalogProps {
  selectedProducts: SelectedProduct[];
  onProductSelect: (product: Product, quantity: number) => void;
  showCachedPricing?: boolean;
  disabled?: boolean;
  className?: string;
}

const ITEMS_PER_PAGE = 20;

export const ProductCatalog: React.FC<ProductCatalogProps> = ({
  selectedProducts,
  onProductSelect,
  showCachedPricing = false,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [yourProductsOnly, setYourProductsOnly] = useState(false);
  // In Stock filter temporarily disabled — setter removed since the toggle is commented out below
  const [inStockOnly] = useState(false);
  const [skipCorrection, setSkipCorrection] = useState(false);
  const { capture } = useProductEvents({ component: "ProductCatalog" });
  const searchResultsRef = useRef<Map<string, number>>(new Map());
  const lastSearchAddedRef = useRef<string>("");

  const hasSearchQuery = searchQuery.trim().length >= 2;

  // The client decides the retrieval engine — `search-engine` is a boolean
  // PostHog flag; true means "use Elasticsearch". useProductSearch reads this
  // same flag internally to pick which route to call
  // (/api/products/search-elasticsearch vs /api/products/search). This read
  // here is only for guessing the loading animation before the first
  // response arrives — see `engine` (the server-reported truth) below.
  const expectsElastic = useFeatureFlagEnabled("search-engine") ?? false;

  // Data sources
  const { data: recentProducts = [], isLoading: recentLoading } = useRecentProducts();

  const {
    products: searchResults,
    totalCount: searchTotalCount,
    hasMore: searchHasMore,
    isLoading: searchLoading,
    isFetching: searchFetching,
    searchId,
    correctedQuery,
    queryChanges,
    rerankModel,
    engine,
  } = useProductSearch({
    searchQuery,
    limit: ITEMS_PER_PAGE * currentPage,
    offset: 0,
    enabled: hasSearchQuery,
    inStockOnly,
    skipCorrection,
    llmRerank: true,
  });

  // Build the set of recent product IDs for the "Your Products" filter
  const recentProductIds = useMemo(
    () => new Set(recentProducts.map((p) => p.uid)),
    [recentProducts],
  );

  // Choose data source: recent products when idle, search results when querying
  const rawProducts = hasSearchQuery ? searchResults : recentProducts;
  // Show the AI animation for the whole duration a rerank search runs — including
  // background refetches (isFetching), not just the first load (isLoading).
  const isAiReranking = hasSearchQuery && searchFetching;
  // Subtle tell that the re-rank ran on the OpenAI backup rather than Claude.
  const isOpenAiRerank = !!rerankModel && rerankModel.startsWith("gpt");
  // Elasticsearch retrieval gets its own (warm) loading animation. Before the
  // server's actual `engine` is known (first search), fall back to the
  // client's own flag guess so the animation doesn't flash between variants.
  const isElasticSearch = engine ? engine === "elastic" : expectsElastic;
  const isLoading = hasSearchQuery ? searchLoading || isAiReranking : recentLoading;

  // Apply client-side filters
  const filteredProducts = useMemo(() => {
    let products = rawProducts;

    if (yourProductsOnly && hasSearchQuery) {
      // Only filter during search — when idle we're already showing recent products
      products = products.filter((p) => recentProductIds.has(p.uid));
    }

    if (inStockOnly && !hasSearchQuery) {
      // inStockOnly is passed to the search API when searching,
      // but for recent products we need to filter client-side
      products = products.filter((p) => p.status === "Stock");
    }

    return products;
  }, [rawProducts, yourProductsOnly, inStockOnly, hasSearchQuery, recentProductIds]);

  // Track search result positions for analytics
  useEffect(() => {
    if (hasSearchQuery && searchResults.length > 0) {
      searchResultsRef.current.clear();
      searchResults.forEach((p, i) => searchResultsRef.current.set(p.uid, i));
    }
  }, [hasSearchQuery, searchResults]);

  const handleProductSelect = useCallback(
    (product: Product, quantity: number) => {
      const existingQty = selectedProducts.find(
        (p) => p.productId === product.uid,
      )?.quantity ?? 0;
      const isNewAdd = existingQty === 0 && quantity > 0;

      if (hasSearchQuery && searchId && isNewAdd) {
        const rank = searchResultsRef.current.get(product.uid) ?? -1;
        const isFirst = lastSearchAddedRef.current !== searchId;
        capture({
          event: "add_searched_product",
          context: {
            productId: product.uid,
            rank,
            searchId,
            isFirstProductAddedForSearchId: isFirst,
          },
        });
        lastSearchAddedRef.current = searchId;
      } else if (!hasSearchQuery && isNewAdd) {
        const rank = filteredProducts.findIndex((p) => p.uid === product.uid);
        capture({
          event: "add_browsed_product",
          context: { productId: product.uid, rank },
        });
      }

      onProductSelect(product, quantity);
    },
    [hasSearchQuery, searchId, selectedProducts, filteredProducts, onProductSelect, capture],
  );

  // Pagination
  const hasMore = hasSearchQuery ? searchHasMore : false; // Recent products don't paginate
  const totalCount = hasSearchQuery ? searchTotalCount : filteredProducts.length;

  const handleLoadMore = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setSkipCorrection(false);
  }, []);

  const handleRevert = useCallback((originalQuery: string) => {
    setSearchQuery(originalQuery);
    setSkipCorrection(true);
  }, []);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, yourProductsOnly, inStockOnly]);

  // Product notes callback
  const getProductNotes = useCallback((product: Product): string | null => {
    if (product.hajocaProfitCenter === "419") {
      return "Hajoca Kohler Anaheim Product";
    }
    return null;
  }, []);

  // Helper to check if a product is in the user's recent purchases
  const isPurchased = useCallback(
    (productId: string) => recentProductIds.has(productId),
    [recentProductIds],
  );

  return (
    <div className={cn("space-y-3", className)}>
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold">{t("components.products.productCatalog.productCatalog")}</h3>
      </div>

      <div className="space-y-3">
        <ProductSearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t("components.products.productCatalog.searchProducts")}
          disabled={disabled}
          correctedQuery={correctedQuery}
          queryChanges={queryChanges}
          onRevert={handleRevert}
        />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">{t("components.products.productCatalog.filter")}</span>
          <FilterChip
            label={t("components.products.productCatalog.yourProducts")}
            active={yourProductsOnly}
            onClick={() => {
              const next = !yourProductsOnly;
              capture({ event: "tab_switched", context: { tab: "Your Products", active: next } });
              setYourProductsOnly(next);
            }}
          />
          {/* In Stock filter temporarily disabled
          <FilterChip
            label={t("components.products.productCatalog.inStockOnly")}
            active={inStockOnly}
            onClick={() => {
              const next = !inStockOnly;
              capture({ event: "tab_switched", context: { tab: "In Stock Only", active: next } });
              setInStockOnly(next);
            }}
          />
          */}
        </div>
      </div>

      <ProductGrid
        products={filteredProducts}
        selectedProducts={selectedProducts}
        onProductSelect={handleProductSelect}
        showCachedPricing={showCachedPricing}
        disabled={disabled}
        isLoading={isLoading}
        aiThinking={isAiReranking}
        isOpenAiRerank={isOpenAiRerank}
        isElasticSearch={isElasticSearch}
        rerankModel={rerankModel}
        hasMore={hasMore}
        totalCount={totalCount}
        onLoadMore={handleLoadMore}
        getProductNotes={getProductNotes}
        isPurchased={isPurchased}
        emptyStateMessage={
          hasSearchQuery
            ? yourProductsOnly
              ? t("components.products.productCatalog.noMatchingProductsFound")
              : t("components.products.productCatalog.noProductsFoundTry")
            : yourProductsOnly
              ? t("components.products.productCatalog.noRecentProductsFound")
              : t("components.products.productCatalog.noProductsAvailable")
        }
      />
    </div>
  );
};

/** Toggle-style filter chip */
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium transition-colors border",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-foreground border-border hover:bg-accent",
      )}
    >
      {label}
    </button>
  );
}
